// hooks/useDomainSelection.ts
import { useState, useEffect } from "react";
import type { Domain } from "@ethos/shared-types";
import { chatApi } from "../services/chatApi";
import { brand } from "@brand";

const STORAGE_KEY = "ethos:selectedDomainSlug";

/** Query-param name a deep link can carry to auto-select a domain, per brand
 *  (e.g. ?base=<slug>). Wins over localStorage so a shared link always lands
 *  the recipient on the intended domain. */
const URL_SLUG_PARAM = brand.domainUrlParam;

/** Read the `?base=<slug>` query param if present. Returns undefined when
 *  the param is missing, empty, or `window` is unavailable (SSR safety). */
function readSlugFromUrl(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const param = new URLSearchParams(window.location.search).get(URL_SLUG_PARAM);
    return param && param.trim() ? param.trim() : undefined;
  } catch {
    return undefined;
  }
}

export interface DomainSelectionOptions {
  /** Restrict available domains to this subset. Empty / undefined = all active domains. */
  allowedDomainSlugs?: string[];
  /** Pre-select this slug. Used as the initial selection when locked is false. */
  defaultDomainSlug?: string;
  /** When true the user cannot change the domain; shouldShowSelector is always false. */
  locked?: boolean;
}

/**
 * Domain Selection Hook
 *
 * Single source of truth for domain state in the webapp.
 * - Fetches active domains once on mount via GET /api/domains
 * - Persists the user's selection to localStorage for continuity across sessions
 * - Validates stored selection against the live domain list on each fetch
 *   (clears if the stored slug no longer matches an active domain)
 * - Accepts optional channel domain routing constraints (DR-100):
 *   allowedDomainSlugs filters the domain list; locked hides the selector;
 *   defaultDomainSlug pre-selects a domain.
 *
 * Derived values:
 * - shouldShowSelector: true only when domains.length > 1 AND locked !== true
 * - effectiveDomainSlug: the slug sent with chat requests
 *     0 domains → undefined (no bases configured)
 *     1 domain  → that domain's slug (auto-selected, no selector shown)
 *     2+ domains → user's selectedSlug — may be undefined when the user
 *                  hasn't picked yet; the backend then either resolves
 *                  a base from the user's message (silent select) or
 *                  asks which base to use. No more "All Bases" path.
 */
export function useDomainSelection(options?: DomainSelectionOptions) {
    const { allowedDomainSlugs, defaultDomainSlug, locked } = options ?? {};

    const [allDomains, setAllDomains] = useState<Domain[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Initialize from `?base=<slug>` URL param if present (wins over storage),
    // else localStorage, else defaultDomainSlug. The URL form is resolved
    // against the fetched domain list below to make sure it's a real slug.
    const [selectedSlug, setSelectedSlugState] = useState<string | undefined>(() => {
        return readSlugFromUrl() ?? localStorage.getItem(STORAGE_KEY) ?? defaultDomainSlug ?? undefined;
    });

    useEffect(() => {
        const urlSlug = readSlugFromUrl();
        chatApi.fetchDomains()
            .then(({ domains: fetched }) => {
                setAllDomains(fetched);

                // URL slug wins if it resolves to an active domain — persist it
                // so a refresh without the query param keeps the same base.
                if (urlSlug) {
                    const match = fetched.find((d) => d.slug === urlSlug && d.isActive);
                    if (match) {
                        setSelectedSlugState(urlSlug);
                        localStorage.setItem(STORAGE_KEY, urlSlug);
                        return;
                    }
                    // URL slug invalid — fall through to stored / default below.
                }

                // Validate stored slug against current active domain list
                const stored = localStorage.getItem(STORAGE_KEY);
                if (stored && !fetched.find((d) => d.slug === stored && d.isActive)) {
                    // Stored slug no longer valid — fall back to default or clear
                    setSelectedSlugState(defaultDomainSlug ?? undefined);
                    localStorage.removeItem(STORAGE_KEY);
                } else if (!stored && defaultDomainSlug) {
                    setSelectedSlugState(defaultDomainSlug);
                }
            })
            .catch((err: unknown) => {
                console.error("useDomainSelection: failed to fetch domains", err);
                // Leave domains as [] — fallback to global search
            })
            .finally(() => {
                setIsLoading(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply defaultDomainSlug when it becomes available (e.g. after Redux/config loads)
    // and no user selection exists in localStorage.
    useEffect(() => {
        if (defaultDomainSlug && !localStorage.getItem(STORAGE_KEY)) {
            setSelectedSlugState(defaultDomainSlug);
        }
    }, [defaultDomainSlug]);

    const setSelectedSlug = (slug: string | undefined) => {
        if (locked) return; // No-op when channel locks the domain
        setSelectedSlugState(slug);
        if (slug) {
            localStorage.setItem(STORAGE_KEY, slug);
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    };

    // Apply allowedDomainSlugs filter when provided and non-empty
    const domains =
        allowedDomainSlugs && allowedDomainSlugs.length > 0
            ? allDomains.filter((d) => allowedDomainSlugs.includes(d.slug))
            : allDomains;

    const shouldShowSelector = !locked && domains.length > 1;

    // effectiveDomainSlug — the value the chat request actually carries.
    //
    // Priority:
    //   locked            → configured default
    //   1 domain loaded   → that domain (auto-selected, selector hidden)
    //   else              → user's selectedSlug
    //
    // The final branch intentionally accepts selectedSlug BEFORE
    // allDomains has resolved from fetchDomains(). Otherwise there's a
    // race: on cold page load, allDomains is [] for the first 200-500ms
    // while /api/domains resolves — during that window this used to
    // return undefined even though selectedSlug was already populated
    // from URL/localStorage/default. Users typing fast enough to hit
    // send during that window would send domain=undefined, get the
    // "Which base?" pre-flight, and be confused because the dropdown
    // clearly showed their base the whole time. Backend re-validates
    // the slug against its own list, so trusting selectedSlug during
    // loading is safe — worst case is same as today (unknown slug →
    // pre-flight).
    const effectiveDomainSlug: string | undefined = locked
        ? (defaultDomainSlug ?? domains[0]?.slug)
        : domains.length === 1
          ? domains[0].slug
          : selectedSlug;

    return {
        domains,
        selectedSlug,
        setSelectedSlug,
        shouldShowSelector,
        effectiveDomainSlug,
        isLoading,
    };
}
